import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { profilesData, profileParameterData, sampleResponse } from "./jsonData";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import NavBarNew from "../NavBarNew";
import { positions, useAlert } from "react-alert";

const ChooseProfiles = () => {
  const history = useHistory();
  const alert = useAlert();
  const [selectedProfiles, setSelectedProfiles] = useState(
    profilesData.map((each) => each.profileName)
  );
  const [abnormalProfiles, setAbnormalProfiles] = useState(["Blood Counts"]);
  const [loader, setLoader] = useState(false);
  const [cohortsData, setCohortsData] = useState("");

  const handleSingleProfileSelection = (event, name) => {
    if (event.target.checked) {
      setSelectedProfiles((prev) => [...prev, name]);
    } else {
      let filteredProfiles = selectedProfiles.filter((each) => each !== name);
      setSelectedProfiles(filteredProfiles);
    }
  };

  const handleAllProfileSelection = (event) => {
    let allProfiles = profilesData.map((each) => each.profileName);
    if (event.target.checked) {
      setSelectedProfiles(allProfiles);
    } else {
      setSelectedProfiles([]);
    }
  };

  const handleAbnormalToggle = (event, name) => {
    if (event.target.checked) {
      let filteredProfiles = abnormalProfiles.filter((each) => each !== name);
      setAbnormalProfiles(filteredProfiles);
    } else {
      setAbnormalProfiles((prev) => [...prev, name]);
    }
  };

  const getParametersData = () => {
    let data = [];
    for (let profile of selectedProfiles) {
      if (abnormalProfiles.includes(profile)) {
        data.push(profileParameterData[profile]?.abnormal);
      } else {
        data.push(profileParameterData[profile]?.normal);
      }
    }
    return data;
  };

  const createPayload = () => {
    let jsonData = {
      report_id: "NG1234",
      org_id: "NG5678",
      bill_date: "2024-03-17 14:03:00",
      latest_bill_date_in_DB: "2024-11-22 18:03:00",
      data: {
        tests_taken: selectedProfiles,
        abnormal_tests: abnormalProfiles,
        all_parameters_observed_values: getParametersData(),
      },
    };
    return jsonData;
  };

  const fetchCohorts = async () => {
    if (!selectedProfiles.length) {
      alert.error(
        <span className="alertError">
          Please choose profile to generate cohorts.
        </span>,
        {
          position: positions.TOP_CENTER,
        }
      );
      return;
    }
    setLoader(true);
    const apiUrl =
      "https://jyxmm1ky78.execute-api.ap-south-1.amazonaws.com/Testing/whatsappMessaging/api/process-cohorts";
    const payload = createPayload();
    console.log("cohorts payload:", payload);
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "API-Key": "abc123",
      },
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch(apiUrl, options);
      const resData = await response.json();
      console.log("response", response, resData);

      if (resData.success && resData?.data) {
        alert.success(
          <span className="alertError">Succesfully fetched cohorts</span>,
          {
            position: positions.TOP_CENTER,
          }
        );
        setCohortsData(resData.data[0]);
        history.replace("/messaging-cohorts", { state: resData.data[0] });
      } else {
        alert.error(<span className="alertError">{resData.message}</span>, {
          position: positions.TOP_CENTER,
        });
      }
    } catch (e) {
      console.log("Error in fetching data", e);

      alert.error(
        <span className="alertError">
          Something went wrong. Please try again.
        </span>,
        {
          position: positions.TOP_CENTER,
        }
      );
    } finally {
      setLoader(false);
    }
  };

  const basePath =
    "https://reportsvg.s3.ap-south-1.amazonaws.com/optimized/optimized/svg/";

  return (
    <div>
      <NavBarNew />
      <Box sx={{ padding: "0px 8%" }} mt={3}>
        <Typography variant="h7">Choose Test Profiles</Typography>
        <Typography variant="body8">
          Choose a combination of profiles to simulate lab test data.
        </Typography>
        <Box mt={2}>
          <TableContainer
            component={Paper}
            sx={{
              width: { md: "70%", sm: "100%" },
              maxHeight: { md: 350, sm: "" },
            }}
          >
            <Table
              sx={{ minWidth: { md: 650, xs: "" } }}
              size="small"
              aria-label="a dense table"
              stickyHeader
            >
              <TableHead>
                <TableRow sx={{ background: "rgba(244, 247, 252, 0.75)" }}>
                  <TableCell sx={{ width: { md: "10%", xs: "" } }}>
                    <Checkbox
                      onChange={(e) => handleAllProfileSelection(e)}
                      checked={selectedProfiles.length === 10}
                    />
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: { md: "30%", xs: "45%" } }}
                  >
                    <Typography variant="h8">Profile Name</Typography>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ width: { md: "30%", xs: "" } }}
                  >
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      gap={{ md: 2, xs: 1 }}
                    >
                      <Typography variant="h8">Health Status</Typography>
                      <Tooltip
                        title="A profile is unhealthy if it has at least 2 abnormal parameters. Otherwise, all parameters are normal for a healthy profile."
                        placement="right"
                        arrow
                      >
                        <InfoIcon sx={{ color: "#3D85D2" }} />
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profilesData.map((each) => (
                  <TableRow
                    key={each.profileName}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": { background: "#DDEDFF" },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      <Checkbox
                        checked={selectedProfiles.includes(each.profileName)}
                        onChange={(e) =>
                          handleSingleProfileSelection(e, each.profileName)
                        }
                      />
                    </TableCell>

                    <TableCell align="left">
                      <Stack direction={"row"} alignItems={"center"} gap={2}>
                        <Box
                          sx={{
                            display: { md: "block", sm: "block", xs: "none" },
                          }}
                        >
                          <img
                            src={`${basePath}${each.icon}`}
                            alt="profile-icons"
                            width="30px"
                          />
                        </Box>
                        <Typography variant="body8">
                          {each.profileName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={0.5}
                        justifyContent={"center"}
                        flexWrap={"wrap"}
                      >
                        <Typography
                          sx={{
                            color: "#F43B3B",
                            display: { md: "block", sm: "block", xs: "none" },
                          }}
                          variant="body8"
                        >
                          Unhealthy
                        </Typography>
                        <Switch
                          checked={!abnormalProfiles.includes(each.profileName)}
                          onChange={(e) =>
                            handleAbnormalToggle(e, each.profileName)
                          }
                          inputProps={{ "aria-label": "controlled" }}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#00A390", // Thumb color when switched on
                              "&:hover": {
                                backgroundColor: "rgba(0, 163, 144, 0.1)", // Hover effect
                              },
                            },
                            "& .MuiSwitch-switchBase": {
                              color: "#F43B3B", // Thumb color when switched off
                              "&:hover": {
                                backgroundColor: "rgba(244, 59, 59, 0.1)", // Hover effect
                              },
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "rgba(0, 163, 144, 0.5)",
                              },
                            "& .MuiSwitch-track": {
                              backgroundColor: "rgba(244, 59, 59, 0.5)", // Color when checked
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            color: "#00A390",
                            display: { md: "block", sm: "block", xs: "none" },
                          }}
                          variant="body8"
                        >
                          Healthy
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box
          mt={2}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: { md: "70%", sm: "100%" },
          }}
        >
          <Button
            variant="custom_msg"
            onClick={fetchCohorts}
            sx={{ width: "160px" }}
            disabled={loader}
          >
            {loader ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Generate Data"
            )}
          </Button>
          {/* <ToastContainer autoClose={3000} /> */}
        </Box>
      </Box>
    </div>
  );
};

export default ChooseProfiles;
